<template>
  <div class="app-project-details">
    <!-- Modern Header with Timeline -->
    <div class="app-project-details__header">
      <div class="app-project-details__header-top">
        <h1 class="app-project-details__title">{{ localProject.name }}</h1>
        
        <!-- View Planning Button - Top Right -->
        <PermissionGuard :permission="'ACCESS_PHASES'">
          <button 
            @click="viewPlanningPrintable"
            class="app-button app-button--white app-button--icon"
            data-testid="project-view-planning-btn"
            :title="$t('projects.planning.viewPlanning')"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 6 2 18 2 18 9"></polyline>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
              <rect x="6" y="14" width="12" height="8"></rect>
            </svg>
            <span>{{ $t('projects.planning.viewPlanning') }}</span>
          </button>
        </PermissionGuard>
      </div>
      
      <!-- Timeline Section -->
      <div class="app-project-details__timeline" v-if="localProject.estimatedStartDate && localProject.estimatedEndDate">
        <div class="timeline-item">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          <span>{{ $t('projects.start') }}: {{ formatDate(localProject.estimatedStartDate) }}</span>
        </div>
        <div class="timeline-progress">
          <div class="timeline-progress-bar" :style="{ width: projectProgress + '%' }"></div>
        </div>
        <div class="timeline-item">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>{{ $t('projects.end') }}: {{ formatDate(localProject.estimatedEndDate) }}</span>
        </div>
        <div 
          class="timeline-badge" 
          :class="{
            'timeline-badge--overdue': daysRemaining !== null && daysRemaining < 0,
            'timeline-badge--urgent': daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7,
            'timeline-badge--warning': daysRemaining !== null && daysRemaining > 7 && daysRemaining <= 30,
            'timeline-badge--normal': daysRemaining !== null && daysRemaining > 30
          }"
          v-if="daysRemaining !== null"
        >
          <span v-if="daysRemaining > 0">
            {{ $t('projects.daysRemaining', { days: Math.abs(daysRemaining) }) }}
          </span>
          <span v-else-if="daysRemaining === 0">
            {{ $t('projects.dueToday') }}
          </span>
          <span v-else>
            {{ $t('projects.overdue') }} ({{ Math.abs(daysRemaining) }} {{ $t('projects.days') }})
          </span>
        </div>
      </div>
    </div>
    
    <div class="app-project-details__body">
      <div class="app-project-details__section">
        <h3 class="app-project-details__section-title">{{ $t('projects.basicInfo') }}</h3>
        
        <div class="app-project-details__layout">
          <!-- LEFT SIDE: Text Fields (2 columns) -->
          <div class="app-project-details__left">
            <div class="app-project-details__info-grid density-compact">
          
          <!-- Name Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
                <span>{{ $t('projects.name') }}</span>
              </div>
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <button type="button" @click="enableEditField('name')" class="app-project-field__edit-btn" :aria-label="$t('common.edit')" data-testid="edit-name-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </PermissionGuard>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <span v-if="!editingFields.name" class="app-project-field__value app-project-field__value--editable" @click="enableEditField('name')" data-testid="project-detail-name">
                  {{ localProject.name }}
                </span>
              </PermissionGuard>
              <span v-if="!editingFields.name && !hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-name">
                {{ localProject.name }}
              </span>
              <div v-if="editingFields.name" class="app-project-field__edit">
                <input 
                  type="text" 
                  v-model="localProject.name" 
                  @keyup.enter="saveField('name')" 
                  @keyup.esc="cancelEdit('name')" 
                  class="app-project-field__input" 
                  autocomplete="off"
                  ref="nameInput"
                  data-testid="name-input"
                />
                <div class="app-project-field__actions">
                  <button @click="saveField('name')" class="app-project-field__action-btn app-project-field__action-btn--save" :aria-label="$t('common.save')" data-testid="save-name-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </button>
                  <button @click="cancelEdit('name')" class="app-project-field__action-btn app-project-field__action-btn--cancel" :aria-label="$t('common.cancel')" data-testid="cancel-name-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="success-indicator" :class="{ show: fieldSaved.name }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          
          <!-- Description Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
                <span>{{ $t('projects.description') }}</span>
              </div>
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <button type="button" @click="enableEditField('description')" class="app-project-field__edit-btn" :aria-label="$t('common.edit')" data-testid="edit-description-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </PermissionGuard>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <span v-if="!editingFields.description" class="app-project-field__value app-project-field__value--editable" @click="enableEditField('description')" data-testid="project-detail-description">
                  {{ localProject.description }}
                </span>
              </PermissionGuard>
              <span v-if="!editingFields.description && !hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-description">
                {{ localProject.description }}
              </span>
              <div v-if="editingFields.description" class="app-project-field__edit">
                <textarea 
                  v-model="localProject.description" 
                  @keyup.enter="saveField('description')" 
                  @keyup.esc="cancelEdit('description')" 
                  class="app-project-field__input app-project-field__input--textarea" 
                  autocomplete="off"
                  ref="descriptionInput"
                  data-testid="description-input"
                ></textarea>
                <div class="app-project-field__actions">
                  <button @click="saveField('description')" class="app-project-field__action-btn app-project-field__action-btn--save" :aria-label="$t('common.save')" data-testid="save-description-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </button>
                  <button @click="cancelEdit('description')" class="app-project-field__action-btn app-project-field__action-btn--cancel" :aria-label="$t('common.cancel')" data-testid="cancel-description-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="success-indicator" :class="{ show: fieldSaved.description }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>

          <!-- Estimated Start Date Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                  <circle cx="8" cy="14" r="1"></circle>
                </svg>
                <span>{{ $t('projects.estimatedStartDate') }}</span>
              </div>
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <button type="button" @click="enableEditField('estimatedStartDate')" class="app-project-field__edit-btn" :aria-label="$t('common.edit')" data-testid="edit-estimated-start-date-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </PermissionGuard>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <span v-if="!editingFields.estimatedStartDate" class="app-project-field__value app-project-field__value--editable" @click="enableEditField('estimatedStartDate')" data-testid="project-detail-start-date">
                  {{ formatDate(localProject.estimatedStartDate) }}
                </span>
              </PermissionGuard>
              <span v-if="!editingFields.estimatedStartDate && !hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-start-date">
                {{ formatDate(localProject.estimatedStartDate) }}
              </span>
              <div v-if="editingFields.estimatedStartDate" class="app-project-field__edit">
                <input 
                    type="date" 
                    v-model="localProject.estimatedStartDate" 
                    @change="saveField('estimatedStartDate')" 
                    @keyup.esc="cancelEdit('estimatedStartDate')" 
                    class="app-project-field__input" 
                    ref="estimatedStartDateInput"
                    data-testid="estimated-start-date-input"
                />
                <div class="app-project-field__actions">
                  <button @click="saveField('estimatedStartDate')" class="app-project-field__action-btn app-project-field__action-btn--save" :aria-label="$t('common.save')" data-testid="save-estimated-start-date-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </button>
                  <button @click="cancelEdit('estimatedStartDate')" class="app-project-field__action-btn app-project-field__action-btn--cancel" :aria-label="$t('common.cancel')" data-testid="cancel-estimated-start-date-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="success-indicator" :class="{ show: fieldSaved.estimatedStartDate }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          
          <!-- Estimated End Date Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                  <path d="M8 14h8M8 18h8"></path>
                </svg>
                <span>{{ $t('projects.estimatedEndDate') }}</span>
              </div>
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <button type="button" @click="enableEditField('estimatedEndDate')" class="app-project-field__edit-btn" :aria-label="$t('common.edit')" data-testid="edit-estimated-end-date-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </PermissionGuard>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <span v-if="!editingFields.estimatedEndDate" class="app-project-field__value app-project-field__value--editable" @click="enableEditField('estimatedEndDate')" data-testid="project-detail-estimated-end-date">
                  {{ formatDate(localProject.estimatedEndDate) }}
                </span>
              </PermissionGuard>
              <span v-if="!editingFields.estimatedEndDate && !hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-estimated-end-date">
                {{ formatDate(localProject.estimatedEndDate) }}
              </span>
              <div v-if="editingFields.estimatedEndDate" class="app-project-field__edit">
                <input 
                    type="date" 
                    v-model="localProject.estimatedEndDate" 
                    @change="saveField('estimatedEndDate')" 
                    @keyup.esc="cancelEdit('estimatedEndDate')" 
                    class="app-project-field__input" 
                    ref="estimatedEndDateInput"
                    data-testid="estimated-end-date-input"
                />
                <div class="app-project-field__actions">
                  <button @click="saveField('estimatedEndDate')" class="app-project-field__action-btn app-project-field__action-btn--save" :aria-label="$t('common.save')" data-testid="save-estimated-end-date-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </button>
                  <button @click="cancelEdit('estimatedEndDate')" class="app-project-field__action-btn app-project-field__action-btn--cancel" :aria-label="$t('common.cancel')" data-testid="cancel-estimated-end-date-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="success-indicator" :class="{ show: fieldSaved.estimatedEndDate }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>

          <!-- GROUP 3: STAKEHOLDERS & RESOURCES -->

          <!-- Client Name Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>{{ $t('projects.projectClientName') }}</span>
              </div>
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <button type="button" @click="enableEditField('clientName')" class="app-project-field__edit-btn" :aria-label="$t('common.edit')" data-testid="edit-client-name-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </PermissionGuard>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <span v-if="!editingFields.clientName" class="app-project-field__value app-project-field__value--editable" @click="enableEditField('clientName')" data-testid="project-detail-client-name">
                  {{ localProject.clientName || $t('notifications.notAvailable') }}
                </span>
              </PermissionGuard>
              <span v-if="!editingFields.clientName && !hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-client-name">
                {{ localProject.clientName || $t('notifications.notAvailable') }}
              </span>
              <div v-if="editingFields.clientName" class="app-project-field__edit">
                <input 
                    type="text" 
                    v-model="localProject.clientName" 
                    @keyup.enter="saveField('clientName')" 
                    @keyup.esc="cancelEdit('clientName')" 
                    class="app-project-field__input" 
                    autocomplete="off"
                    ref="clientNameInput"
                    data-testid="client-name-input"
                />
                <div class="app-project-field__actions">
                  <button @click="saveField('clientName')" class="app-project-field__action-btn app-project-field__action-btn--save" :aria-label="$t('common.save')" data-testid="save-client-name-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </button>
                  <button @click="cancelEdit('clientName')" class="app-project-field__action-btn app-project-field__action-btn--cancel" :aria-label="$t('common.cancel')" data-testid="cancel-client-name-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="success-indicator" :class="{ show: fieldSaved.clientName }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>

          <!-- Location Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>{{ $t('projects.location') }}</span>
              </div>
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <button type="button" @click="enableEditField('location')" class="app-project-field__edit-btn" :aria-label="$t('common.edit')" data-testid="edit-location-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </PermissionGuard>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <span v-if="!editingFields.location" class="app-project-field__value app-project-field__value--editable" @click="enableEditField('location')" data-testid="project-detail-location">
                  {{ localProject.location || $t('notifications.notAvailable') }}
                </span>
              </PermissionGuard>
              <span v-if="!editingFields.location && !hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-location">
                {{ localProject.location || $t('notifications.notAvailable') }}
              </span>
              <div v-if="editingFields.location" class="app-project-field__edit">
                <input 
                    type="text" 
                    v-model="localProject.location" 
                    @keyup.enter="saveField('location')" 
                    @keyup.esc="cancelEdit('location')" 
                    class="app-project-field__input" 
                    autocomplete="off"
                    ref="locationInput"
                    data-testid="location-input"
                />
                <div class="app-project-field__actions">
                  <button @click="saveField('location')" class="app-project-field__action-btn app-project-field__action-btn--save" :aria-label="$t('common.save')" data-testid="save-location-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </button>
                  <button @click="cancelEdit('location')" class="app-project-field__action-btn app-project-field__action-btn--cancel" :aria-label="$t('common.cancel')" data-testid="cancel-location-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="success-indicator" :class="{ show: fieldSaved.location }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
          
          <!-- Budget Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                <span>{{ budgetLabel }}</span>
              </div>
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <button type="button" @click="enableEditField('budget')" class="app-project-field__edit-btn" :aria-label="$t('common.edit')" data-testid="edit-budget-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                </button>
              </PermissionGuard>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <span v-if="!editingFields.budget" class="app-project-field__value app-project-field__value--editable" @click="enableEditField('budget')" data-testid="project-detail-budget">
                  {{ localProject.budget || $t('notifications.notAvailable') }}
                </span>
              </PermissionGuard>
              <span v-if="!editingFields.budget && !hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-budget">
                {{ localProject.budget || $t('notifications.notAvailable') }}
              </span>
              <div v-if="editingFields.budget" class="app-project-field__edit">
                <input 
                    type="number" 
                    v-model="localProject.budget" 
                    @keyup.enter="saveField('budget')" 
                    @keyup.esc="cancelEdit('budget')" 
                    class="app-project-field__input" 
                    autocomplete="off"
                    ref="budgetInput"
                    data-testid="budget-input"
                />
                <div class="app-project-field__actions">
                  <button @click="saveField('budget')" class="app-project-field__action-btn app-project-field__action-btn--save" :aria-label="$t('common.save')" data-testid="save-budget-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </button>
                  <button @click="cancelEdit('budget')" class="app-project-field__action-btn app-project-field__action-btn--cancel" :aria-label="$t('common.cancel')" data-testid="cancel-budget-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="success-indicator" :class="{ show: fieldSaved.budget }">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          </div>
            </div>
            <!-- End of left grid -->
          </div>
          <!-- End of left side -->
          
          <!-- RIGHT SIDE: Status & Multi-Phase (1 column) -->
          <div class="app-project-details__right">
            <div class="app-project-details__info-grid-single density-compact">
          
          <!-- Status Field with Workflow Timeline -->
          <StatusTimeline
            :title="$t('projects.statusWorkflow')"
            :current-status="localProject.status"
            :timeline="statusTimeline"
            :possible-statuses="possibleStatuses"
            :loading="loadingStatuses"
            i18n-prefix="projects.statusOptions"
            action-i18n-prefix="projects.statusActions"
            transition-to-key="projects.statusTransition.transitionTo"
            update-permission="UPDATE_PROJECT_STATUS"
            @refresh="loadProjectStatusData"
          >
            <template #modal="{ show, onSuccess, onClose }">
              <StatusTransitionModal
                :is-visible="show"
                :project-id="props.project?.id"
                :current-status="localProject.status"
                @close="onClose"
                @success="onSuccess"
              />
            </template>
          </StatusTimeline>

          <!-- Multi-Phase Toggle Card -->
          <PermissionGuard permission="ACCESS_PHASES">
            <div class="app-project-field info-card">
              <div class="app-project-field__header">
                <div class="app-project-field__label">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="8" y1="6" x2="21" y2="6"></line>
                    <line x1="8" y1="12" x2="21" y2="12"></line>
                    <line x1="8" y1="18" x2="21" y2="18"></line>
                    <line x1="3" y1="6" x2="3.01" y2="6"></line>
                    <line x1="3" y1="12" x2="3.01" y2="12"></line>
                    <line x1="3" y1="18" x2="3.01" y2="18"></line>
                  </svg>
                  <span>{{ $t('projects.configuration') }}</span>
                </div>
              </div>
              
              <div class="app-project-field__content">
                <PermissionGuard permission="UPDATE_PROJECT_INFO">
                  <div class="toggle-container">
                    <div class="toggle-info">
                      <div class="toggle-title">{{ $t('projects.multiPhase') }}</div>
                      <div class="toggle-description">{{ $t('projects.multiPhaseDescription') }}</div>
                    </div>
                    <div 
                      class="toggle-switch" 
                      :class="{ active: localProject.multiPhase }"
                      @click="toggleMultiPhase"
                      role="switch"
                      :aria-checked="localProject.multiPhase"
                      tabindex="0"
                      @keydown.enter="toggleMultiPhase"
                      @keydown.space.prevent="toggleMultiPhase"
                      data-testid="multi-phase-toggle"
                    >
                      <div class="toggle-knob"></div>
                    </div>
                  </div>
                  
                  <!-- Phase Preview -->
                  <div class="phase-preview" :class="{ visible: localProject.multiPhase }">
                    <div class="workflow-title">{{ $t('projects.phasePreview') }}</div>
                    <div v-if="loadingPhases" class="phase-loading">
                      <span>{{ $t('common.loading') }}...</span>
                    </div>
                    <div v-else-if="projectPhases.length > 0" class="phase-list">
                      <div 
                        v-for="(phase, index) in projectPhases" 
                        :key="phase.id || index"
                        class="phase-item"
                      >
                        <div class="phase-number">{{ index + 1 }}</div>
                        <span>{{ phase.name }}</span>
                      </div>
                    </div>
                    <div v-else class="phase-empty">
                      <span>{{ $t('projects.noPhases') }}</span>
                    </div>
                  </div>
                </PermissionGuard>
                
                <div v-if="!hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-multi-phase">
                  {{ localProject.multiPhase ? $t('projects.multiPhaseEnabled') : $t('projects.multiPhaseDisabled') }}
                </div>
              </div>
            </div>
          </PermissionGuard>

          <!-- Management Type Card -->
          <div class="app-project-field info-card">
            <div class="app-project-field__header">
              <div class="app-project-field__label">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
                <span>{{ $t('projects.managementType') }}</span>
              </div>
            </div>
            <div class="app-project-field__content">
              <PermissionGuard permission="UPDATE_PROJECT_INFO">
                <div class="management-type-selector" data-testid="management-type-selector">
                  <label
                    v-for="option in managementTypeOptions"
                    :key="option.value"
                    class="management-type-option"
                    :class="{ 'management-type-option--selected': (localProject as any).managementType === option.value }"
                    :data-testid="`management-type-option-${option.value.toLowerCase()}`"
                  >
                    <input
                      type="radio"
                      :value="option.value"
                      :checked="(localProject as any).managementType === option.value"
                      @change="selectManagementType(option.value)"
                      class="management-type-option__radio"
                    />
                    <div class="management-type-option__content">
                      <div class="management-type-option__label">{{ option.label }}</div>
                      <div class="management-type-option__description">{{ option.description }}</div>
                    </div>
                  </label>
                </div>
              </PermissionGuard>
              <div v-if="!hasPermissionForProject('UPDATE_PROJECT_INFO', project?.id).value" class="app-project-field__value" data-testid="project-detail-management-type">
                {{ $t(`projects.managementTypeOptions.${(localProject as any).managementType || 'PILOTAGE'}`) }}
              </div>
            </div>
          </div>
            </div>
            <!-- End of right grid -->
          </div>
          <!-- End of right side -->
        </div>
        <!-- End of layout -->
      </div>
      <!-- End of section -->
      
      <div class="app-project-details__actions">
        <div class="app-project-details__feedback" v-if="hasChanges">
          {{ $t('projects.unsavedChanges') }}
        </div>
        <div class="app-project-details__action-buttons">
          <PermissionGuard :permission="'UPDATE_PROJECT_INFO'">
            <button 
              @click="saveChanges"
              data-testid="project-save-changes-btn"
              :class="['app-button', hasChanges ? 'app-button--primary' : 'app-button--disabled']"
              :disabled="!hasChanges || isSaving"
            >
              <span v-if="isSaving">
                <div class="app-loading-spinner--small"></div>
                {{ $t('common.saving') }}
              </span>
              <span v-else>
                {{ $t('common.saveChanges') }}
              </span>
            </button>
          </PermissionGuard>
        </div>
      </div>
      <!-- End of actions -->
    </div>
    <!-- End of body -->
  </div>
  <!-- End of app-project-details -->
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { reactive, ref, nextTick, watch, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { usePermissions } from '@/composables/usePermissions';
import { useNotifications } from '@/composables/useNotifications';
import ProjectService from '@/services/project.service';
import projectPhasesService from '@/services/projectPhases.service';
import PermissionGuard from '../../common/PermissionGuard.vue';
import StatusTransitionModal from './ProjectStatusTransitionModal.vue';
import StatusTimeline from '@/components/common/StatusTimeline.vue';
import type { Project } from '@/types';
import type { ProjectStatusHistoryDto } from '@/types/project/audit';
import { getUserCurrency, getCurrencySymbol } from '@/utils/currencyUtils';

const props = defineProps<{
  project: Project;
  originalProject: Partial<Project>;
}>();

const emit = defineEmits<{
  (e: 'update:project', project: Project): void;
  (e: 'saved', project: Project): void;
}>();

const { t } = useI18n();
const router = useRouter();
const { hasPermissionForProject } = usePermissions();
const { notifySuccess, notifyError } = useNotifications();
const isSaving = ref<boolean>(false);
const hasChanges = ref<boolean>(false);

// New UX features
const fieldSaved = reactive<Record<string, boolean>>({
  name: false,
  description: false,
  location: false,
  estimatedStartDate: false,
  estimatedEndDate: false,
  clientName: false,
  budget: false
});

// Phase data
const projectPhases = ref<any[]>([]);
const loadingPhases = ref<boolean>(false);

// Status transition state
const possibleStatuses = ref<string[]>([]);

// Project status timeline
const statusTimeline = ref<ProjectStatusHistoryDto[]>([]);
const loadingStatuses = ref<boolean>(false);

// Create a reactive copy of the project to prevent mutating props directly
const localProject = reactive<Project>({ ...props.project } as Project);

// Ensure managementType has a default value
if (!(localProject as any).managementType) {
  (localProject as any).managementType = 'PILOTAGE';
}

const editingFields = reactive<Record<string, boolean>>({
  name: false,
  description: false,
  status: false,
  location: false,
  creationDate: false,
  estimatedStartDate: false,
  estimatedEndDate: false,
  clientName: false,
  budget: false
});

// Refs for input elements
const nameInput = ref<HTMLInputElement | null>(null);
const descriptionInput = ref<HTMLTextAreaElement | null>(null);
const statusInput = ref<HTMLSelectElement | null>(null);
const locationInput = ref<HTMLInputElement | null>(null);
const creationDateInput = ref<HTMLInputElement | null>(null);
const estimatedStartDateInput = ref<HTMLInputElement | null>(null);
const estimatedEndDateInput = ref<HTMLInputElement | null>(null);
const clientNameInput = ref<HTMLInputElement | null>(null);
const budgetInput = ref<HTMLInputElement | null>(null);

// Computed properties for timeline
const projectProgress = computed(() => {
  if (!localProject.estimatedStartDate || !localProject.estimatedEndDate) return 0;
  
  const start = new Date(localProject.estimatedStartDate).getTime();
  const end = new Date(localProject.estimatedEndDate).getTime();
  const now = Date.now();
  
  if (now < start) return 0;
  if (now > end) return 100;
  
  const total = end - start;
  const elapsed = now - start;
  return Math.round((elapsed / total) * 100);
});

const daysRemaining = computed(() => {
  if (!localProject.estimatedEndDate) return null;
  
  // Parse the end date and set to end of day (23:59:59) to avoid timezone issues
  const endDate = new Date(localProject.estimatedEndDate);
  endDate.setHours(23, 59, 59, 999);
  
  // Get current date at start of day (00:00:00) for fair comparison
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const diff = endDate.getTime() - now.getTime();
  
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
});

// Budget label with currency
const budgetLabel = computed(() => {
  const userCurrency = getUserCurrency();
  const currencySymbol = getCurrencySymbol(userCurrency);
  return `${t('projects.projectBudget')} (${currencySymbol})`;
});

// Management type options for the selector
const managementTypeOptions = computed(() => [
  {
    value: 'PILOTAGE',
    label: t('projects.managementTypeOptions.PILOTAGE'),
    description: t('projects.managementTypeDescriptions.PILOTAGE')
  },
  {
    value: 'FULL_MANAGEMENT',
    label: t('projects.managementTypeOptions.FULL_MANAGEMENT'),
    description: t('projects.managementTypeDescriptions.FULL_MANAGEMENT')
  }
]);

const selectManagementType = (value: string) => {
  (localProject as any).managementType = value;
  checkChanges();
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return t('notifications.notAvailable') as string;
  try {
    const date = new Date(dateString as string);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date format received: ${dateString}`);
      return t('notifications.invalidDate') as string;
    }
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  } catch (e) {
    console.error('Error formatting date:', e);
    // Note: Silent error for date formatting - no user notification needed for display issues
    return t('common.error') as string;
  }
};

const checkChanges = () => {
  if (!props.originalProject || Object.keys(props.originalProject).length === 0) {
    hasChanges.value = false;
    return;
  }

  let changed = false;
  // Compare only relevant, editable fields
  const fieldsToCompare: Array<keyof Project | string> = [
    'name', 'description', 'status', 'location', 'creationDate',
    'estimatedStartDate', 'estimatedEndDate', 'icon',
    'clientName', 'budget', 'multiPhase', 'managementType'
  ];

  for (const key of fieldsToCompare) {
    // Use any indexing for dynamic keys
    if ((localProject as any)[key] !== (props.originalProject as any)[key]) {
      changed = true;
      break;
    }
  }

  hasChanges.value = changed;
};

const enableEditField = (field: string) => {
  editingFields[field] = true;
  nextTick(() => {
    if (field === 'name' && nameInput.value) nameInput.value.focus();
    if (field === 'description' && descriptionInput.value) descriptionInput.value.focus();
    if (field === 'status' && statusInput.value) statusInput.value.focus();
    if (field === 'location' && locationInput.value) locationInput.value.focus();
    if (field === 'creationDate' && creationDateInput.value) creationDateInput.value.focus();
    if (field === 'estimatedStartDate' && estimatedStartDateInput.value) estimatedStartDateInput.value.focus();
    if (field === 'estimatedEndDate' && estimatedEndDateInput.value) estimatedEndDateInput.value.focus();
    if (field === 'clientName' && clientNameInput.value) clientNameInput.value.focus();
    if (field === 'budget' && budgetInput.value) budgetInput.value.focus();
  });
};

const cancelEdit = (field: string) => {
  // Revert to the original value
  (localProject as any)[field] = (props.originalProject as any)[field];
  editingFields[field] = false;
  checkChanges();
};

const saveField = (field: string) => {
  editingFields[field] = false;
  checkChanges();
  
  // Show success indicator
  if (field in fieldSaved) {
    fieldSaved[field] = true;
    setTimeout(() => {
      fieldSaved[field] = false;
    }, 2000);
  }
};

const toggleMultiPhase = async () => {
  // Toggle the boolean value
  const previousValue = localProject.multiPhase;
  localProject.multiPhase = !localProject.multiPhase;
  
  checkChanges();
  
  // Save immediately when toggle is changed
  isSaving.value = true;
  try {
    const payload = { ...(localProject as any) } as any;
    const savedData = await ProjectService.updateProject(String(payload.id), payload) as any;

    // Update the project data with the local copy
    const merged = { ...(localProject as any), ...(savedData || {}) } as Project;
    emit('update:project', merged);
    emit('saved', merged);

    hasChanges.value = false;
  } catch (error) {
    console.error('Error saving multi-phase setting:', error);
    notifyError(t('projects.save.multiPhase.error'));
    // Revert to previous value on error
    localProject.multiPhase = previousValue;
  } finally {
    isSaving.value = false;
  }
};

const saveChanges = async () => {
  isSaving.value = true;

  try {
    const payload = { ...(localProject as any) } as any;
    const savedData = await ProjectService.updateProject(String(payload.id), payload) as any;

    // Update the project data with the local copy
    const merged = { ...(localProject as any), ...(savedData || {}) } as Project;
    emit('update:project', merged);
    emit('saved', merged);

    hasChanges.value = false;
    Object.keys(editingFields).forEach(key => (editingFields as any)[key] = false);
    notifySuccess(t('projects.save.success'));
  } catch (error) {
    console.error('Error saving project data:', error);
    notifyError(t('projects.save.error'));
  } finally {
    isSaving.value = false;
  }
};

const viewPlanningPrintable = () => {
  const route = router.resolve({
    name: 'ProjectPlanningPrintable',
    params: { projectId: String(props.project?.id) }
  });
  window.open(route.href, '_blank');
};

// Watch for changes in the project prop data
watch(
  () => props.project,
  (newValue: Project) => {
    // Update localProject with changes from props
    Object.assign(localProject, newValue);
    checkChanges();
  },
  { deep: true }
);

// Watch for changes in the original project data
watch(
  () => props.originalProject,
  (newValue: Partial<Project>) => {
    // When original values change (like after saving), reset comparison base
    console.log('Original project data changed:', newValue);
    checkChanges();
  },
  { deep: true }
);

// Check for changes on component mount
checkChanges();

// Load project phases
const loadProjectPhases = async () => {
  if (!props.project?.id || !localProject.multiPhase || !hasPermissionForProject('ACCESS_PHASES', props.project?.id).value) {
    projectPhases.value = [];
    return;
  }
  
  loadingPhases.value = true;
  try {
    const phases = await projectPhasesService.getPhases(String(props.project.id));
    projectPhases.value = phases || [];
  } catch (error) {
    console.error('Failed to load project phases:', error);
    notifyError(t('projects.phases.loadError'));
    projectPhases.value = [];
  } finally {
    loadingPhases.value = false;
  }
};

// Watch multiPhase to load phases when enabled
watch(
  () => localProject.multiPhase,
  (isMultiPhase: boolean) => {
    if (isMultiPhase && hasPermissionForProject('ACCESS_PHASES', props.project?.id).value) {
      loadProjectPhases();
    } else {
      projectPhases.value = [];
    }
  }
);

// Load possible statuses on mount
onMounted(async () => {
  // Load project-specific status timeline
  if (props.project?.id) {
    await loadProjectStatusData();
    
    // Load phases if multiPhase is enabled and user has permission
    if (localProject.multiPhase && hasPermissionForProject('ACCESS_PHASES', props.project?.id).value) {
      await loadProjectPhases();
    }
  }
});

// Load project status timeline and possible statuses
const loadProjectStatusData = async () => {
  if (!props.project?.id) return;
  
  loadingStatuses.value = true;
  try {
    const timeline = await ProjectService.getProjectStatusTimeline(String(props.project.id));
    statusTimeline.value = timeline || [];
    
    // After status change, reload the project to get the updated current status
    const response = await ProjectService.getProjectById(String(props.project.id));
    if (response?.data) {
      // Update local project with the new status
      Object.assign(localProject, response.data);
      // Notify parent component
      emit('update:project', response.data);
      emit('saved', response.data);
    }
  } catch (error) {
    console.error('Failed to load project status timeline:', error);
    notifyError(t('projects.statusData.timeline.loadError'));
    statusTimeline.value = [];
  } finally {
    loadingStatuses.value = false;
  }

  // Load possible status transitions
  try {
    const statuses = await ProjectService.getPossibleStatuses(String(props.project.id));
    possibleStatuses.value = statuses || [];
  } catch (error) {
    console.error('Failed to load possible statuses:', error);
    notifyError(t('projects.statusData.possibleStatuses.loadError'));
    possibleStatuses.value = [];
  }
};

// Note: handleStatusChange is no longer needed - StatusTimeline component handles it internally
// The @refresh event from StatusTimeline will trigger loadProjectStatusData
</script>

<style scoped>
/* Modern CSS Variables - Ensure consistent theming */
.app-project-details {
  /* Colors */
  --primary: #3b82f6;
  --primary-dark: #2563eb;
  --primary-light: #dbeafe;
  --success: #10b981;
  --success-light: #d1fae5;
  --warning: #f59e0b;
  --error: #ef4444;
  --info: #06b6d4;
  
  /* Neutrals */
  --neutral-50: #fafafa;
  --neutral-100: #f5f5f5;
  --neutral-200: #e5e5e5;
  --neutral-800: #262626;
  
  /* Surface */
  --surface: #ffffff;
  --surface-hover: #f9fafb;
  --surface-secondary: #f3f4f6;
  --border-color: #e5e7eb;
  
  /* Text */
  --text-primary: #111827;
  --text-secondary: #6b7280;
  --text-muted: #9ca3af;
  
  /* Spacing */
  --space-xs: 0.25rem;
  --space-sm: 0.5rem;
  --space-md: 1rem;
  --space-lg: 1.5rem;
  --space-xl: 2rem;
  --space-2xl: 3rem;
  
  /* Border Radius */
  --border-radius: 0.5rem;
  --border-radius-sm: 0.25rem;
  --border-radius-lg: 0.75rem;
  
  /* Component Structure */
  padding: 0;
  background: var(--surface);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
}

/* Two-column layout */
.app-project-details__layout {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: var(--space-xl);
  align-items: start;
}

.app-project-details__left,
.app-project-details__right {
  min-width: 0; /* Prevent grid overflow */
}

.app-project-details__info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-lg);
}

.app-project-details__info-grid-single {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-lg);
}

/* Compact layout styling */
.app-project-details__info-grid.density-compact,
.app-project-details__info-grid-single.density-compact {
  gap: var(--space-md);
}

@media (max-width: 1200px) {
  .app-project-details__layout {
    grid-template-columns: 1fr;
  }
  
  .app-project-details__info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .app-project-details__info-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .app-project-details__header-top {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-sm);
  }

  .app-project-details__title {
    min-width: 0;
    word-break: break-word;
  }
}
/* Header with Gradient */
.app-project-details__header {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark, #2563eb) 100%);
  color: white;
  padding: var(--space-xl);
  position: relative;
  overflow: hidden;
}

.app-project-details__header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: pulse 3s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
}

.app-project-details__header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
  margin-bottom: var(--space-sm);
}

.app-project-details__title {
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 0;
  position: relative;
  z-index: 1;
  flex: 1;
}

.app-button--icon {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  white-space: nowrap;
}

.app-button--icon svg {
  flex-shrink: 0;
}

.app-button--white {
  background: white;
  color: var(--primary);
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.app-button--white:hover {
  background: rgba(255, 255, 255, 0.95);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Timeline Section */
.app-project-details__timeline {
  position: relative;
  z-index: 1;
  margin-top: var(--space-lg);
  display: flex;
  align-items: center;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.timeline-item {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: 0.875rem;
  opacity: 0.95;
}

.timeline-progress {
  flex: 1;
  min-width: 150px;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 9999px;
  overflow: hidden;
  position: relative;
}

.timeline-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
  border-radius: 9999px;
  transition: width 0.5s ease;
  position: relative;
  animation: progressPulse 2s ease-in-out infinite;
}

@keyframes progressPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.85; }
}

.timeline-progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.timeline-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: var(--space-xs) var(--space-md);
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.timeline-badge--overdue {
  background: var(--error, #ef4444);
  color: white;
  animation: pulse 2s ease-in-out infinite;
}

.timeline-badge--urgent {
  background: var(--warning, #f59e0b);
  color: white;
}

.timeline-badge--warning {
  background: rgba(251, 191, 36, 0.3);
  color: white;
}

.timeline-badge--normal {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Body Section */
.app-project-details__body {
  padding: var(--space-xl);
  margin-bottom: 0;
}



.app-project-details__section {
  margin-bottom: var(--space-xl);
}

.app-project-details__section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--space-lg);
  color: var(--text-primary);
  padding-bottom: var(--space-sm);
  border-bottom: 1px solid var(--border-color);
}

/* Grid with Density Support - Removed to use new layout */

/* Info Card - Modern Design */
.info-card {
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg, 0.75rem);
  padding: var(--space-lg);
  transition: all 0.2s ease;
  position: relative;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.info-card:hover {
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -2px rgb(0 0 0 / 0.05);
  border-color: var(--primary-light, #dbeafe);
  transform: translateY(-2px);
}

/* Edit button shows on card hover */
.info-card .app-project-field__edit-btn {
  opacity: 0;
  transition: all 0.2s ease;
}

.info-card:hover .app-project-field__edit-btn,
.info-card:focus-within .app-project-field__edit-btn {
  opacity: 1;
}

.density-compact .info-card {
  padding: var(--space-md);
}

.density-spacious .info-card {
  padding: var(--space-xl);
}

.app-project-field {
  margin-bottom: var(--space-lg);
}

.app-project-field__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--space-sm);
}

.app-project-field__label {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.app-project-field__label svg {
  width: 18px;
  height: 18px;
  color: var(--primary);
  flex-shrink: 0;
  stroke-width: 2;
}

.app-project-field__content {
  position: relative;
}

.app-project-field__value {
  display: flex;
  align-items: center;
  padding: var(--space-sm) var(--space-md);
  min-height: 2.5rem;
  color: var(--text-primary);
  word-break: break-word;
  text-align: left;
  line-height: var(--line-height-base);
  border-radius: var(--border-radius-sm, 0.25rem);
  border: 1px solid transparent;
  background: transparent;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: 500;
}

.app-project-field__value--editable {
  cursor: pointer;
}

.app-project-field__value--editable:hover {
  background: var(--surface-hover, #f9fafb);
  border-color: var(--primary-light, #dbeafe);
}

.info-card:hover .app-project-field__value {
  background: var(--surface-hover, #f9fafb);
}

.app-project-field__edit-btn {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  position: relative;
  color: var(--text-muted, #9ca3af);
  transition: all 0.2s ease;
  border-radius: var(--border-radius-sm, 0.25rem);
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-project-field__edit-btn svg {
  width: 16px;
  height: 16px;
  stroke: currentColor;
  pointer-events: none;
}

.app-project-field__edit-btn:hover {
  background: var(--surface-hover, #f9fafb);
  color: var(--primary);
  transform: scale(1.1);
}

.app-project-field__edit-btn:focus {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.app-project-field__btn-icon {
  width: 16px;
  height: 16px;
}

.app-project-field__tooltip {
  position: absolute;
  right: calc(100% + var(--space-xs));
  background: var(--neutral-800);
  color: white;
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all var(--transition-duration) ease;
  z-index: 10;
}

.app-project-field__edit-btn:hover .app-project-field__tooltip,
.app-project-field__action-btn:hover .app-project-field__tooltip {
  opacity: 1;
  visibility: visible;
}

.app-project-field__edit {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
}

.app-project-field__input {
  flex: 1;
  padding: var(--space-sm) var(--space-md);
  min-height: 2.5rem;
  border: 2px solid var(--primary);
  border-radius: var(--border-radius-sm, 0.25rem);
  font-size: 1rem;
  font-family: inherit;
  color: var(--text-primary);
  background: var(--surface);
  transition: all 0.2s ease;
  line-height: 1.5;
  box-shadow: 0 0 0 4px var(--primary-light);
}

.app-project-field__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 6px var(--primary-light);
}

.app-project-field__input--textarea {
  min-height: 80px;
  resize: vertical;
  line-height: var(--line-height-base);
  padding: var(--space-sm) var(--space-md);
}

.app-project-field__input--select {
  cursor: pointer;
  padding: var(--space-sm) var(--space-md);
}

/* Modern Toggle Switch Styles */
.app-project-field__switch-wrapper {
  padding: var(--space-md);
  border-radius: var(--border-radius-sm);
  border: 1px solid var(--border-color);
  transition: all var(--transition-duration) ease;
  width: 100%;
}

.app-project-field__switch-wrapper:hover {
  background: var(--surface-hover);
  border-color: var(--primary-light);
}

.app-project-field__switch-label {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--space-md);
  cursor: pointer;
  user-select: none;
}

.app-project-field__switch-text {
  flex: 1;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  line-height: var(--line-height-base);
  text-align: left;
}

.app-project-field__switch {
  position: relative;
  width: 52px;
  height: 28px;
  flex-shrink: 0;
}

.app-project-field__switch-input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.app-project-field__switch-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--neutral-300);
  border-radius: 28px;
  transition: all 0.3s ease;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.app-project-field__switch-slider::before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.app-project-field__switch-input:checked + .app-project-field__switch-slider {
  background-color: var(--primary);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15);
}

.app-project-field__switch-input:checked + .app-project-field__switch-slider::before {
  transform: translateX(24px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.app-project-field__switch-input:focus + .app-project-field__switch-slider {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.app-project-field__switch-input:disabled + .app-project-field__switch-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.app-project-field__switch-label:hover .app-project-field__switch-slider {
  background-color: var(--neutral-400);
}

.app-project-field__switch-label:hover .app-project-field__switch-input:checked + .app-project-field__switch-slider {
  background-color: var(--primary-dark);
}

.app-project-field__actions {
  display: flex;
  gap: var(--space-sm);
  margin-top: var(--space-sm);
}

.app-project-field__action-btn {
  min-width: 36px;
  min-height: 36px;
  background: transparent;
  border: none;
  padding: var(--space-sm);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-sm, 0.25rem);
  transition: all 0.2s ease;
  font-weight: 600;
  font-size: 0.875rem;
}

.app-project-field__action-btn svg {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
}

.app-project-field__action-btn:hover {
  transform: translateY(-1px) scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.app-project-field__action-btn:focus {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}

.app-project-field__action-btn--save {
  color: white;
  background: var(--success, #10b981);
}

.app-project-field__action-btn--save:hover {
  background: #059669;
}

.app-project-field__action-btn--cancel {
  color: white;
  background: var(--neutral-400, #a3a3a3);
}

.app-project-field__action-btn--cancel:hover {
  background: var(--neutral-500, #737373);
}

.app-project-details__actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--border-color);
}

.app-project-details__feedback {
  color: var(--warning);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.app-project-details__action-buttons {
  display: flex;
  gap: var(--space-sm);
}

.app-loading-spinner--small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-left-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: var(--space-xs);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .app-project-details__info-grid {
    grid-template-columns: 1fr;
    gap: var(--space-lg);
  }
  
  .app-project-field__edit {
    flex-direction: column;
    align-items: stretch;
  }
  
  .app-project-field__actions {
    flex-direction: row;
    justify-content: center;
  }
  
  .app-project-details__actions {
    flex-direction: column;
    gap: var(--space-sm);
    align-items: stretch;
  }
}

/* Success Indicator */
.success-indicator {
  position: absolute;
  top: var(--space-md);
  right: var(--space-md);
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: var(--success, #10b981);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0.5);
  transition: all 0.3s ease;
}

.success-indicator.show {
  opacity: 1;
  transform: scale(1);
  animation: checkmark 0.5s ease forwards;
}

@keyframes checkmark {
  0% {
    opacity: 0;
    transform: scale(0.5) rotate(45deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.1) rotate(45deg);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}

/* Hover effects for cards */
.info-card:hover .app-project-field__value {
  background: var(--surface-hover, #f9fafb);
}

/* Multi-Phase Toggle Card */
.toggle-card {
  background: linear-gradient(135deg, var(--info-light, #cffafe) 0%, var(--surface) 100%);
  border-color: var(--info, #06b6d4);
}

.toggle-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-md);
  background: white;
  border-radius: var(--border-radius-lg, 0.75rem);
  border: 2px dashed var(--info, #06b6d4);
  margin-top: var(--space-sm);
}

.toggle-info {
  flex: 1;
}

.toggle-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--space-xs);
}

.toggle-description {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.toggle-switch {
  position: relative;
  width: 60px;
  height: 32px;
  background: var(--neutral-300, #d4d4d4);
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toggle-switch:hover {
  opacity: 0.9;
}

.toggle-switch.active {
  background: var(--primary);
}

.toggle-knob {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 9999px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.toggle-switch.active .toggle-knob {
  transform: translateX(28px);
}

/* Phase Preview */
.phase-preview {
  margin-top: var(--space-lg);
  padding: 0;
  background: white;
  border-radius: var(--border-radius-lg, 0.75rem);
  border: 1px solid var(--border-color);
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.phase-preview.visible {
  opacity: 1;
  max-height: 400px;
  padding: var(--space-md);
}

.workflow-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: var(--space-md);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.phase-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}

.phase-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm);
  background: var(--surface-hover, #f9fafb);
  border-radius: var(--border-radius-sm, 0.25rem);
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.phase-item:hover {
  background: var(--surface-secondary, #f3f4f6);
  transform: translateX(4px);
}

.phase-number {
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: var(--primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* StatusTimeline container needs proper containment */
.app-project-details__right .status-timeline-container {
  /* Container stays within bounds */
  max-width: 100%;
  overflow: hidden; /* Container itself doesn't overflow */
}

.app-project-details__right .status-timeline {
  /* Timeline scrolls within its container */
  overflow-x: scroll !important;
  overflow-y: hidden !important;
  /* Ensure scrollbar is always visible */
  scrollbar-width: auto !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .app-project-details__info-grid {
    grid-template-columns: 1fr;
  }
  
  .app-project-details__timeline {
    flex-direction: column;
    align-items: stretch;
  }
  
  .timeline-progress {
    order: -1;
  }
}

/* Management type selector inside ProjectInfo */
.management-type-selector {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  padding-top: var(--space-xs);
}

.management-type-option {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.management-type-option:hover {
  border-color: var(--primary);
  background-color: var(--primary-light);
}

.management-type-option--selected {
  border-color: var(--primary);
  background-color: var(--primary-light);
}

.management-type-option__radio {
  margin-top: 3px;
  accent-color: var(--primary);
  flex-shrink: 0;
}

.management-type-option__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.management-type-option__label {
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
}

.management-type-option__description {
  font-size: var(--font-size-xs, 0.75rem);
  color: var(--text-secondary);
  line-height: 1.3;
}
</style>
